import { useRef, useState } from "react";
import styled from "@emotion/styled";
import { GrIcon, GrStack, GrTypography } from "@jnpmedi/grebob";
import { palette } from "@jnpmedi/grebob/dist/theme/palette";
import { getFileUploadSizes, MvnMessage } from "@newLibs/utils";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useOrganizationContext } from "@newHooks/context/organization/organization.context";
import { enqueueSnackbar } from "@newHooks/context/snackbar/notification.context";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { DropSectionProgressBar } from "./DropSectionProgressBar";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderFileCriteria } from "@maven-rest/docs/FolderFile.dto";

export type FileUploadStatus = "WAIT" | "UPLOADING" | "DONE" | "ERROR";

interface AttachmentDropBoxProps {
  folderId?: bigint;
  folderType?: FolderType;
  children?: React.ReactNode;
}

export function DropSection({ folderId, folderType = FolderType.NORMAL, children }: AttachmentDropBoxProps) {
  const { organization } = useOrganizationContext();

  const [files, setFiles] = useState<Array<File>>([]);
  const isDragging = useBoolean(false);
  const isUploadStatusBarVisible = useBoolean(false);

  const draggingElementCount = useRef<number>(0);

  const { uploadSizeLimitInMB, uploadSizeLimitInMBForHostOS } = getFileUploadSizes(organization?.licenses || []);

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    draggingElementCount.current++;

    if (draggingElementCount.current === 1) {
      isDragging.onTrue();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const validFileList = Array.from(event.dataTransfer.files).filter((f) => f.size > 0);

    if (!validFileList.length) {
      enqueueSnackbar({ message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_UPLOAD_ZERO_BYTES), severity: "warning" });
    } else {
      if (validFileList.length > FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT) {
        enqueueSnackbar({
          message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_MAX_COUNT_HELPER_TEXT, {
            count: FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT,
          }),
          severity: "danger",
        });
      } else {
        setFiles(validFileList);
        isUploadStatusBarVisible.onTrue();
      }
    }

    draggingElementCount.current = 0;
    isDragging.onFalse();
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    draggingElementCount.current--;

    if (draggingElementCount.current === 0) {
      isDragging.onFalse();
    }
  };

  const handleUploadFloatingBarClose = () => {
    setFiles([]);
    isDragging.onFalse();
    isUploadStatusBarVisible.onFalse();
  };

  if (!organization) {
    return null;
  }

  return (
    <GrStack
      style={{ position: "relative" }}
      flexDirection="column"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!!children && <div style={{ opacity: isDragging.value ? 0.6 : 1 }}>{children}</div>}
      <DropBoxWrapper isDragging={isDragging.value} absoluteTopOffset={baseHeight.appHeaderHeight}>
        <GrStack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={4}
          width="100%"
          height="100%"
          style={{ display: isDragging.value ? "flex" : "none" }}
        >
          <GrIcon type="visual-file-upload" size={152} />
          <GrStack
            flexDirection="column"
            alignItems="center"
            padding="16px 48px"
            borderRadius="90px"
            backgroundColor={palette.darkNavy040}
            color={palette.white}
          >
            <GrTypography color="white" type="Content.Title02">
              {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_UPLOAD_DND_HELPER_DESCRIPTION)}
            </GrTypography>
            <GrTypography color="white" type="Content.Body03">
              {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_MAX_COUNT_HELPER_TEXT, {
                count: FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT,
              })}
            </GrTypography>
          </GrStack>
        </GrStack>
      </DropBoxWrapper>
      {!!files.length && isUploadStatusBarVisible.value && (
        <div style={{ position: "absolute", right: 6, bottom: 6 }}>
          <DropSectionProgressBar
            organizationId={organization.id}
            folderId={folderId}
            folderType={folderType}
            files={files}
            onClose={handleUploadFloatingBarClose}
            uploadSizeLimitInMB={uploadSizeLimitInMB}
            uploadSizeLimitInMBForHostOS={uploadSizeLimitInMBForHostOS}
          />
        </div>
      )}
    </GrStack>
  );
}

const DropBoxWrapper = styled.div<{ isDragging: boolean; absoluteTopOffset: string }>`
  position: absolute;
  display: ${({ isDragging }) => (isDragging ? "block" : "none")};
  width: 100%;
  height: calc(100% - ${baseHeight.appHeaderHeight});
  top: ${({ absoluteTopOffset }) => absoluteTopOffset};
  left: 0;
  background-color: rgba(185, 225, 255, 0.2);
  border-radius: 6px;
  border: 2px dashed ${palette.darkNavy040};
`;
